import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, fmtTime, fmtTimeRange } from "@/lib/format";
import { ChevronLeft, ChevronRight, MapPin, Lock } from "lucide-react";

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfWeek(d) { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

/**
 * Cross-production room schedule. Every admin/staff sees every booking so
 * they can plan around one another — but rehearsals from productions the
 * viewer doesn't belong to are masked to a generic label. Rows = rooms,
 * columns = days-of-week. One glance answers "is the stage free next Thursday?"
 */
export default function FacilitySchedule({ embedded }) {
  const [cursor, setCursor] = useState(new Date());
  const [facilities, setFacilities] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [filterFacility, setFilterFacility] = useState("");

  const weekStart = useMemo(() => startOfWeek(cursor), [cursor]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const start = useMemo(() => ymd(weekDays[0]), [weekDays]);
  const end = useMemo(() => ymd(weekDays[6]), [weekDays]);

  useEffect(() => {
    (async () => {
      const [f, s] = await Promise.all([api.get("/facilities"), api.get("/spaces")]);
      setFacilities(f.data);
      setSpaces(s.data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const params = { start_date: start, end_date: end };
      if (filterFacility) params.facility_id = filterFacility;
      const r = await api.get("/facilities/schedule", { params });
      setBookings(r.data);
    })();
  }, [start, end, filterFacility]);

  const shownSpaces = filterFacility ? spaces.filter((s) => s.facility_id === filterFacility) : spaces;
  const byRoomByDay = useMemo(() => {
    const m = {};
    for (const b of bookings) {
      m[b.space_id] ||= {};
      m[b.space_id][b.date] ||= [];
      m[b.space_id][b.date].push(b);
    }
    for (const sid of Object.keys(m)) {
      for (const d of Object.keys(m[sid])) {
        m[sid][d].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
      }
    }
    return m;
  }, [bookings]);

  const shift = (n) => {
    const next = new Date(cursor);
    next.setDate(next.getDate() + n * 7);
    setCursor(next);
  };

  return (
    <div className={embedded ? "space-y-3" : "space-y-6"} data-testid="facility-schedule">
      {!embedded && (
        <header>
          <h1 className="font-display text-3xl tracking-tight font-light">Room Schedule</h1>
          <p className="text-zinc-400 text-sm mt-1">Cross-production view of every room.</p>
        </header>
      )}

      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-1">
          <button data-testid="schedule-prev" onClick={() => shift(-1)} className="h-8 w-8 rounded border border-white/10 hover:bg-white/5"><ChevronLeft className="w-4 h-4 mx-auto" /></button>
          <button data-testid="schedule-today" onClick={() => setCursor(new Date())} className="h-8 px-3 rounded border border-white/10 hover:bg-white/5 text-xs uppercase tracking-widest">This week</button>
          <button data-testid="schedule-next" onClick={() => shift(1)} className="h-8 w-8 rounded border border-white/10 hover:bg-white/5"><ChevronRight className="w-4 h-4 mx-auto" /></button>
          <div className="ml-3 font-display text-lg tracking-tight text-zinc-200">{fmtDate(start)} — {fmtDate(end)}</div>
        </div>
        <div>
          <select
            value={filterFacility}
            onChange={(e) => setFilterFacility(e.target.value)}
            className="h-8 rounded bg-[#0A0A0A] border border-white/10 text-xs px-2"
            data-testid="schedule-facility-filter"
          >
            <option value="">All facilities</option>
            {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      </div>

      {shownSpaces.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-8 text-center text-sm text-zinc-500">
          No rooms yet. Ask an admin to add facilities and rooms from the “Rooms & Facilities” tab.
        </div>
      ) : (
        <div className="rounded-lg border border-white/10 bg-[#0F0F0F] overflow-x-auto">
          <div className="grid" style={{ gridTemplateColumns: `220px repeat(7, minmax(140px, 1fr))` }}>
            <div className="p-2 border-b border-r border-white/5 text-[10px] uppercase tracking-[0.24em] text-zinc-500">Room</div>
            {weekDays.map((d) => (
              <div key={d.toISOString()} className="p-2 border-b border-white/5 text-[10px] uppercase tracking-[0.24em] text-zinc-500 border-r last:border-r-0">
                <div>{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                <div className="text-zinc-300 mt-0.5">{String(d.getMonth() + 1).padStart(2, "0")}-{String(d.getDate()).padStart(2, "0")}</div>
              </div>
            ))}
            {shownSpaces.map((s) => {
              const fac = facilities.find((f) => f.id === s.facility_id);
              return (
                <div key={s.id} className="contents">
                  <div className="p-2 border-b border-r border-white/5 flex items-start gap-2" data-testid={`schedule-row-${s.id}`}>
                    <MapPin className="w-3.5 h-3.5 text-gold mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{s.name}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{fac?.name}</div>
                    </div>
                  </div>
                  {weekDays.map((d) => {
                    const dateStr = ymd(d);
                    const items = byRoomByDay[s.id]?.[dateStr] || [];
                    return (
                      <div key={dateStr} className="p-1.5 border-b border-r border-white/5 last:border-r-0 min-h-[56px] space-y-1">
                        {items.map((b) => (
                          <div key={b.id}
                            className={`rounded px-1.5 py-1 text-[10px] leading-tight ${b.can_edit ? "bg-gold/10 border border-gold/25 text-gold" : "bg-white/5 border border-white/10 text-zinc-400"}`}
                            data-testid={`schedule-booking-${b.id}`}
                            title={`${b.title} · ${fmtTimeRange(b.start_time, b.end_time)}${b.production_title ? " · " + b.production_title : ""}`}>
                            <div className="font-mono">{fmtTime(b.start_time)}</div>
                            <div className="truncate flex items-center gap-1">
                              {!b.can_edit && <Lock className="w-2.5 h-2.5 shrink-0" />}
                              <span className="truncate">{b.title}</span>
                            </div>
                            {b.production_title && <div className="text-[9px] opacity-70 truncate">{b.production_title}</div>}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-[11px] text-zinc-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Bookings by other productions appear in grey — you can see the time slot but not the show title.</p>
    </div>
  );
}
